import "./profileManagement.css";
import React, { useState, useEffect } from "react";
import {
  Form,
  Row,
  Col,
  Button,
  Table,
  Modal,
  Spinner,
  Container
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  BsTrash,
  BsPlusCircle,
} from "react-icons/bs"; // Importa le icone necessarie
import { useAppKeycloak } from "../../keycloak/keycloakUtils";
import { ServiceFactory } from "../../Api/Services/ServiceFactory";
import { ServiceNames } from "../../Api/registry/Services";
import { useToast } from "../../components/ToastContext/ToastContext";
import { useDispatch, useSelector } from 'react-redux';
import { fetchUserData } from '../../store/slices/personSlice';

const ProfileManagement = () => {
  const { t } = useTranslation(["usersetting"]);
  const { keycloak } = useAppKeycloak();
  const { showToast } = useToast();
  const [userService, setUserService] = useState(null);
  const [userData, setUserData] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newProfile, setNewProfile] = useState("");
  const [availableProfiles, setAvailableProfiles] = useState([]);
  const dispatch = useDispatch();
  const userMeta = useSelector((state) => state.person?.userData);
  const userMetaLoading = useSelector((state) => state.person?.userDataLoading);
  const userMetaError = useSelector((state) => state.person?.userDataError);
  
  useEffect(() => {
    const initializeServices = async () => {
      try {
        const service = await ServiceFactory.getService(ServiceNames.UserService);
        setUserService(service);
      } catch (error) {
        showToast(error.message || t("error:wrong"), "error");
      }
    };
    initializeServices();
  }, []);

// Quando cambia lo store, sincronizza lo stato locale per l'editing
useEffect(() => {
    if (!userMeta) return;
    // normalizza: nello store è "roles", il componente usa "profiles"
    const profiles = userMeta.roles || userMeta.profiles || [];
    setUserData({ ...userMeta, profiles });
    const allProfiles = ["user", "expert", "machine"];
    setAvailableProfiles(allProfiles.filter((p) => !profiles.includes(p)));
  }, [userMeta]);

  const handleDeleteProfile = (profile) => {
    if (!userData?.profiles || userData.profiles.length === 1) {
      showToast(t("usersetting:messages.oneprofilemandatory"), "warning");
      return;
    }

    // Aggiorniamo solo lo stato locale
    setUserData(prevUserData => ({
      ...prevUserData,
      profiles: prevUserData.profiles.filter(p => p !== profile),
    }));

    // Aggiorniamo la lista dei profili disponibili
    setAvailableProfiles(prevProfiles => [...prevProfiles, profile]);
  };

  const handleAddProfile = () => {

    if (!newProfile) return;

    // Aggiorniamo solo lo stato locale
    setUserData(prevUserData => ({
      ...prevUserData,
      profiles: [...(prevUserData?.profiles || []), newProfile],
    }));

    // Rimuoviamo il profilo aggiunto da quelli disponibili
    setAvailableProfiles(availableProfiles.filter(p => p !== newProfile));
    setNewProfile("");
    setShowAddModal(false);
  };

  const handleSaveProfiles = async () => {
    try {
      // Creiamo l'oggetto UpdateUserProfileRequest con la lista dei roleNames
      const updateRequest = {
        roles: userData.profiles || []  // già contiene l'array di stringhe ["user", "expert", etc.]
      };
      
      // Inviamo la lista al backend
      await userService.updateUserProfiles(updateRequest);
      showToast(t("usersetting:messages.success"), "success");
      await keycloak.updateToken(15811200)
      await dispatch(fetchUserData()).unwrap();
    } catch (error) {
      showToast(error.message || t("error:wrong"), "error");
    }
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setNewProfile("");
  };

  const handleShowAddModal = () => {
    setShowAddModal(true);
  };

  if (userMetaLoading || !userData) {
    return (
      <Spinner animation="border" role="status" className="d-flex m-auto">
        <span className="visually-hidden">{t("usersetting:messages.loading")}</span>
      </Spinner>
    ); // Mostra un indicatore di caricamento
  }

 if (userMetaError) {
    return <div>{t("error:wrong")} {userMetaError}</div>;
 }  

  return (
    <Container fluid style={{ position: 'relative', height: '100%', maxHeight: '100%' }}>
      <Row>
        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
          <Table className="profile-management-table" striped bordered hover responsive variant="dark">
            <thead>
              <tr>
                <th>{t("usersetting:caption.tableprofile")}</th>
                <th>{t("usersetting:caption.tabledescription")}</th>
                <th>{t("usersetting:caption.tableoperations")}</th>
              </tr>
            </thead>
            <tbody>
              {userData?.profiles.map((profile) => (
                <tr key={profile}>
                  <td>{profile}</td>
                  <td>{t(`usersetting:${profile}profiledescription`)}</td>
                  <td>
                    <Button
                      variant="link"
                      onClick={() => handleDeleteProfile(profile)}
                      title={t("usersetting:tooltip.deleteprofile")}
                    >
                      <BsTrash />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </Row>

      <div style={{ position: 'absolute', bottom: '0px', display: 'flex', gap: '10px', alignItems: 'center' }}>
        <Button
          variant="success"
          onClick={handleShowAddModal}
        >
          <BsPlusCircle className="me-1" /> {t("usersetting:button.caption.addprofile")}
        </Button>

        <Button variant="primary" onClick={handleSaveProfiles}>
          {t("usersetting:button.caption.save")}
        </Button>
      </div>

      <Modal show={showAddModal} onHide={handleCloseAddModal}>
        <Modal.Header closeButton>
          <Modal.Title>{t("usersetting:caption.modaltitle")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>{t("usersetting:select.selectProfile")}</Form.Label>
            <Form.Select
              value={newProfile}
              onChange={(e) => setNewProfile(e.target.value)}
            >
              <option value="">{t("usersetting:select.selectProfile")}</option>
              {availableProfiles.map((profile) => (
                <option key={profile} value={profile}>
                  {profile}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseAddModal}>
            {t("usersetting:button.caption.cancel")}
          </Button>
          <Button variant="primary" onClick={handleAddProfile} disabled={!newProfile}>
            {t("usersetting:button.caption.addprofile")}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ProfileManagement;